import { IProto } from "../hooks/useTextureLoader";

type PromiseCache<T> = {
  promise: Promise<void>
  url: string,
  error?: any,
  proto?: IProto<T>,
  response?: T
}

type PromiseFn<T> = (proto: IProto<T>, url: string) => Promise<T>

const globalCache: PromiseCache<any>[] = []

function handleAsset<T>(
  fn: PromiseFn<T>,
  cache: PromiseCache<T>[],
  throwError: boolean,
  url: string,
  proto?: IProto<T>,
) {
  for (const entry of cache) {
    // Find a match
    if (url === entry.url) {
      // If an error occurred, throw
      if (entry.error && throwError) throw entry.error
      // If an error occurred but throwing suppressed, return
      if (entry.error && !throwError) return null
      // If a response was successful, return
      if (entry.response) return entry.response
      // If the promise is still unresolved, throw
      throw entry.promise
    }
  }

  // The request is new or has changed.
  const entry: PromiseCache<T> = {
    url,
    proto,
    promise:
      // Make the promise request.
      fn(proto as IProto<T>, url)
        // Response can't be undefined or else the loop above wouldn't be able to return it
        // This is for promises that do not return results (delays for instance)
        .then((response) => (entry.response = (response ?? true) as T))
        .catch((e) => (entry.error = e ?? new Error('unknown error'))),
  }
  cache.push(entry)
  throw entry.promise
}

function loadAsset<T>(fn: PromiseFn<T>, url: string, throwError = false, proto: IProto<any>): T {
  return handleAsset(fn, globalCache as PromiseCache<T>[], throwError, url, proto) as T;
}

/**
 * Drop every RESOLVED texture whose URL the project no longer references, disposing the GPU texture.
 *
 * The counterpart to `evictModelsNotIn` and subject to the same rule: only call it once React has
 * unmounted whatever was rendering these, or a live mesh is left holding a disposed texture (which
 * renders blank or black, silently).
 *
 * `isEvictable` is REQUIRED, not optional, and exists to stop this from being a footgun. This cache holds
 * more than project media — bundled UI assets like the broken-image icon load through the same path, and
 * they never appear in any content doc, so a bare "not in the keep-set" rule would dispose them on the
 * first update and permanently break them. The caller, which knows what a project asset URL looks like,
 * decides what is even a candidate.
 *
 * Entries still in flight are left alone — a suspended component is waiting on that promise. Errored
 * entries are dropped so a transient failure can be retried once the asset is back.
 */
function evictTexturesNotIn(keepUrls: Set<string>, isEvictable: (url: string) => boolean): string[] {
  const evicted: string[] = []

  for (let i = globalCache.length - 1; i >= 0; i--) {
    const entry = globalCache[i]
    if (keepUrls.has(entry.url)) continue
    if (!isEvictable(entry.url)) continue
    // Unresolved: something is suspended on it. Leave it.
    if (!entry.response && !entry.error) continue

    const texture = entry.response as { isTexture?: boolean; dispose?: () => void } | undefined
    if (texture?.isTexture) texture.dispose?.()
    globalCache.splice(i, 1)
    evicted.push(entry.url)
  }

  return evicted
}

export { loadAsset, evictTexturesNotIn }